/**
 * Earnings reporting for the affiliate portal and the control panel.
 *
 * This module is the single place that knows what the API can answer. Both read
 * schemas are `additionalProperties: false`, so a field that is not in the
 * document is a 400 rather than a filtered result — flip a flag here only once
 * the matching field exists.
 *
 * `AffiliateGetAllRequest` and `AffiliateReferralCodeGetListRequest` now take a
 * date interval, and the referral-code envelope reports the paid/unpaid
 * breakdown. Neither accepts a payment-state filter, so that control stays
 * disabled: narrowing rows to "paid only" would have to be invented client-side
 * from figures the row endpoints do not carry.
 */
import type {
  AffiliateEarningsFilter,
  AffiliateEarningsPaymentState,
  AffiliateEarningsRangeId,
  AffiliateEarningsSummary,
} from './affiliateTypes';

/** Flip a flag only once the matching field exists in the API document. */
export const AFFILIATE_EARNINGS_SUPPORT = {
  /** `startDate`/`endDate` on both read schemas. */
  dateRange: true,
  /** No request schema filters rows by paid/due. */
  paymentState: false,
} as const;

export const AFFILIATE_EARNINGS_RANGE_IDS: readonly AffiliateEarningsRangeId[] = [
  'allTime',
  'last7Days',
  'last30Days',
  'thisMonth',
  'lastMonth',
  'custom',
];

export const AFFILIATE_EARNINGS_PAYMENT_STATES: readonly AffiliateEarningsPaymentState[] =
  ['all', 'paid', 'due'];

/** `yyyy-mm-dd` in local time — `toISOString` would shift the day west of UTC. */
const toDayValue = (date: Date): string => {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
};

const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

/**
 * Start/end days for a preset. `allTime` and `custom` carry no bounds of their
 * own: the first is unbounded, the second is whatever the operator typed.
 */
export const resolveAffiliateEarningsRange = (
  rangeId: AffiliateEarningsRangeId,
  today: Date = new Date(),
): { startDate: string; endDate: string } => {
  const endOfToday = toDayValue(today);

  switch (rangeId) {
    // `last7Days` includes today, so it reaches back six days, not seven.
    case 'last7Days':
      return { startDate: toDayValue(addDays(today, -6)), endDate: endOfToday };
    case 'last30Days':
      return { startDate: toDayValue(addDays(today, -29)), endDate: endOfToday };
    case 'thisMonth':
      return {
        startDate: toDayValue(new Date(today.getFullYear(), today.getMonth(), 1)),
        endDate: endOfToday,
      };
    case 'lastMonth': {
      const firstOfLastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
      // Day 0 of this month is the last day of the previous one.
      const lastOfLastMonth = new Date(today.getFullYear(), today.getMonth(), 0);
      return {
        startDate: toDayValue(firstOfLastMonth),
        endDate: toDayValue(lastOfLastMonth),
      };
    }
    default:
      return { startDate: '', endDate: '' };
  }
};

export const createAffiliateEarningsFilter = (
  rangeId: AffiliateEarningsRangeId = 'allTime',
  today: Date = new Date(),
): AffiliateEarningsFilter => ({
  rangeId,
  ...resolveAffiliateEarningsRange(rangeId, today),
  paymentState: 'all',
});

/** Moving the preset re-derives the bounds; `custom` keeps the typed ones. */
export const withAffiliateEarningsRange = (
  filter: AffiliateEarningsFilter,
  rangeId: AffiliateEarningsRangeId,
  today: Date = new Date(),
): AffiliateEarningsFilter => {
  if (rangeId === 'custom') {
    return { ...filter, rangeId };
  }
  return { ...filter, rangeId, ...resolveAffiliateEarningsRange(rangeId, today) };
};

/**
 * Why a range cannot be sent. `incomplete` and `inverted` are separated because
 * picking "Custom" carries the previous preset's bounds over — empty ones when
 * that preset was `allTime` — so telling the operator the start date is after
 * the end date before they have typed anything describes a problem they do not
 * have.
 */
export type AffiliateEarningsRangeIssue = 'none' | 'incomplete' | 'inverted';

export const affiliateEarningsRangeIssue = (
  filter: AffiliateEarningsFilter,
): AffiliateEarningsRangeIssue => {
  if (filter.rangeId === 'allTime') {
    return 'none';
  }
  if (!filter.startDate || !filter.endDate) {
    return 'incomplete';
  }
  return filter.startDate <= filter.endDate ? 'none' : 'inverted';
};

/** A bounded range is only usable once both ends parse and are in order. */
export const isAffiliateEarningsRangeValid = (
  filter: AffiliateEarningsFilter,
): boolean => affiliateEarningsRangeIssue(filter) === 'none';

const dayBoundary = (day: string, endOfDay: boolean): string | null => {
  const parts = day.split('-').map(Number);
  if (parts.length !== 3 || parts.some(part => !Number.isFinite(part))) {
    return null;
  }
  const [year, month, date] = parts;
  const instant = endOfDay
    ? new Date(year, month - 1, date, 23, 59, 59, 999)
    : new Date(year, month - 1, date, 0, 0, 0, 0);
  return Number.isNaN(instant.getTime()) ? null : instant.toISOString();
};

/**
 * The interval as the API takes it: `date-time` bounds covering the operator's
 * local days, so a range picked in Kolkata does not silently start in UTC.
 *
 * `dateFilterType` is left unset. It names a server-side preset and the API
 * document publishes no vocabulary for it, so guessing a value would risk a
 * filter the operator did not ask for; explicit bounds say exactly what is meant.
 */
export const toAffiliateEarningsRangePayload = (
  filter: AffiliateEarningsFilter,
): { startDate: string | null; endDate: string | null } => {
  if (
    !AFFILIATE_EARNINGS_SUPPORT.dateRange ||
    filter.rangeId === 'allTime' ||
    !isAffiliateEarningsRangeValid(filter)
  ) {
    return { startDate: null, endDate: null };
  }
  return {
    startDate: dayBoundary(filter.startDate, false),
    endDate: dayBoundary(filter.endDate, true),
  };
};

/** Payment figures an endpoint reported; omitted ones stay `null` in the tiles. */
export interface AffiliatePaymentTotals {
  paidEarnings?: number;
  dueEarnings?: number;
  unclaimedEarnings?: number;
  lifetimeEarnings?: number;
  lifetimePaidEarnings?: number;
  /** Commission held back until the pending referrals qualify. */
  pendingEarnings?: number;
}

/**
 * The signup breakdown an endpoint reported. Kept apart from the payment totals
 * because they come from different parts of the envelope and a deployment can
 * answer one without the other.
 */
export interface AffiliateQualificationTotals {
  totalSignups?: number;
  eligibleCount?: number;
  pendingCount?: number;
  rejectedCount?: number;
  reversedCount?: number;
}

const reported = (value: number | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

export const buildAffiliateEarningsSummary = (
  totals: { totalEarnings: number; usageCount: number },
  payments?: AffiliatePaymentTotals,
  qualification?: AffiliateQualificationTotals,
): AffiliateEarningsSummary => ({
  totalEarnings: totals.totalEarnings,
  usageCount: totals.usageCount,
  paidEarnings: reported(payments?.paidEarnings),
  dueEarnings: reported(payments?.dueEarnings),
  unclaimedEarnings: reported(payments?.unclaimedEarnings),
  lifetimeEarnings: reported(payments?.lifetimeEarnings),
  lifetimePaidEarnings: reported(payments?.lifetimePaidEarnings),
  pendingEarnings: reported(payments?.pendingEarnings),
  // `usageCount` is the legacy alias of `totalSignups`, so a deployment that
  // reports only the old field still fills the Signups tile rather than
  // leaving it blank next to a populated Eligible one.
  totalSignups: reported(qualification?.totalSignups) ?? totals.usageCount,
  eligibleCount: reported(qualification?.eligibleCount),
  pendingCount: reported(qualification?.pendingCount),
  rejectedCount: reported(qualification?.rejectedCount),
  reversedCount: reported(qualification?.reversedCount),
});
